import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { styleDesktop, styleMobile } from './UploadScreen.style';
import { ROOT_DOMAIN } from '@env';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import {
  SharedProjectSettings,
  ImageShareStatusUpdate,
  ImageShareError,
  ImageShareInvalidToken,
  ImageShareExpiredAuthToken,
  ImageShareFailure,
  ImageShareUploadFailure,
  ImageShareCorruptedImage,
} from '@photosi/expo-photosi-sdk/build/PhotosiSdk';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { allowAllExit } from 'src/_shared/utils/prevent-exit/preventExit';
import { WEBSITE_BASE_URL, PHS_TOKEN_COOKIE, USER_TOKEN_COOKIE } from 'src/app/settings';
import { useStore } from 'src/contexts/StoreContext';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import jsCookies from 'js-cookie';
import { ComponentMap, UploadState } from './UploadScreen.constants';
import { UploadScreenProps } from './UploadScreen.types';
import { SourcesEventType } from '../modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { ILLUSTRATION_NAMES } from '../illustration/illustration.helper';
import { reloadPage } from 'src/_shared/utils/reload-page/reloadPage';
import UploadErrorCode from 'src/helpers/upload.helper';

export const useUploadScreenLogic = ({ sourceEventType, token, origin }: UploadScreenProps) => {
  const { i18n, project, toastStore } = useStore();
  const [uploadingState, setUploadingState] = useState<UploadState>(UploadState.UPLOAD);
  const [progress, setProgress] = useState<number>(0);
  const nPhotos = useRef<number>(0);
  const nPhotosError = useRef<number>(0);
  const corruptedFiles = useRef<string[]>([]);
  const [files, setFiles] = useState<PickedImage[]>([]);
  const [settings, setSettings] = useState<SharedProjectSettings>({ projectGuid: '', thumbnailSize: '' });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { isDesktop } = useResponsiveScreen();
  const style = isDesktop ? styleDesktop : styleMobile;
  const isPrePicker = uploadingState === UploadState.RETRY;

  useEffect(() => {
    allowAllExit();
    subscribeEvents();
    project.getSharedProjectSettings(token).then(resp => {
      setSettings(resp);
      setIsLoading(false);
    });
    amplitude.trackEvent(AnalyticsEvents.sharePhoto.pageView, {
      share_token_id: token,
      source_type: sourceEventType,
      origin,
      product_group: '',
      section_name: '',
    });

    return () => unsubscribeEvents();
  }, []);

  const onPressUpload = useCallback(async () => {
    setFiles([]);
    setProgress(0);
    nPhotos.current = 0;
    amplitude.trackEvent(AnalyticsEvents.sharePhoto.selectClick, {
      share_token_id: token,
      source_type: sourceEventType,
      origin,
      product_group: '',
      section_name: '',
    });
    const productImages: PickedImage[] = await pickImages({
      allowsMultipleSelection: true,
      i18n,
      errorCallback: ({ errorMessage }) => {
        toastStore.showErrorToast({
          id: 'alert-uploading-photos-error-toast',
          message: errorMessage,
        });
      },
      newRoute: true,
    });
    if (productImages.length) {
      nPhotosError.current = 0;
      corruptedFiles.current = [];
      setFiles(productImages);
      // Logic for file.source mapping
      const fileSource = sourceEventType === SourcesEventType.FamilyFriends ? 'family-friends' : sourceEventType;
      productImages.forEach(file => (file.source = fileSource));
      setUploadingState(UploadState.UPLOADING);
      nPhotos.current = productImages.length;
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadStart, {
        share_token_id: token,
        source_type: sourceEventType,
        origin,
        n_photo: productImages.length,
        product_group: '',
        section_name: '',
      });
      project.addSharedPhotos({
        shareToken: token,
        images: productImages,
        projectId: settings.projectGuid,
        thumbnailSize: settings.thumbnailSize,
      });
    }
  }, [settings, sourceEventType, token, origin]);

  const onPressRetry = useCallback(async () => {
    setIsLoading(true);
    project.getSharedProjectSettings(token).then(resp => {
      setSettings(resp);
      setIsLoading(false);
    });
    setUploadingState(UploadState.UPLOAD);
  }, [token]);

  const onPressRetryUpload = useCallback(async () => {
    const fileSource = sourceEventType === SourcesEventType.FamilyFriends ? 'family-friends' : sourceEventType;
    files.forEach(file => (file.source = fileSource));
    setUploadingState(UploadState.UPLOADING);
    nPhotos.current = files.length;
    amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadStart, {
      share_token_id: token,
      source_type: sourceEventType,
      origin,
      n_photo: files.length,
      product_group: '',
      section_name: '',
    });
    project.addSharedPhotos({
      shareToken: token,
      images: files,
      projectId: settings.projectGuid,
      thumbnailSize: settings.thumbnailSize,
    });
  }, [settings, sourceEventType, token, origin, files]);

  const onPressRedirectToPhotosiCta = useCallback(async () => {
    window.location.href = WEBSITE_BASE_URL;
  }, []);

  const subscribeEvents = () => {
    project.sdk.getEventEmitter().addListener('ImageShareStatusUpdate', (event: ImageShareStatusUpdate) => {
      console.log('#event - DataModel(ImageShareStatusUpdate) - event: ', event);
      setProgress(parseInt(event.progress.toFixed(0)));
      if (event.progress === 100) {
        if (nPhotosError.current > 0) {
          // if error file are only corrupted files
          if (corruptedFiles.current.length === nPhotosError.current) setUploadingState(UploadState.UPLOAD_CORRUPTED);
          else setUploadingState(UploadState.UPLOAD_AGAIN);
        } else {
          setUploadingState(UploadState.UPLOADED);
        }
        if (nPhotos.current > 0) {
          amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadComplete, {
            share_token_id: token,
            source_type: sourceEventType,
            origin,
            n_photo: nPhotos.current,
            n_photo_success: nPhotos.current - nPhotosError.current,
            n_photo_error: nPhotosError.current,
            product_group: '',
            section_name: '',
          });
        }
      }
    });
    project.sdk.getEventEmitter().addListener('ImageShareError', (event: ImageShareError) => {
      console.log('#event - DataModel(ImageShareError) - event: ', event);
      nPhotosError.current = nPhotosError.current + event.error.length;
    });
    project.sdk.getEventEmitter().addListener('ImageShareCorruptedImage', (event: ImageShareCorruptedImage) => {
      console.log('#event - DataModel(ImageShareCorruptedImage) - event: ', event);
      corruptedFiles.current = event.error;
      nPhotosError.current = nPhotosError.current + event.error.length;
      let errors: string[] = [];
      event.errorCodes?.forEach(error => {
        errors.push(UploadErrorCode.get(error) ?? '');
      });
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadError, {
        source_type: sourceEventType,
        error: errors,
        product_group: '',
        section_name: '',
      });
      setUploadingState(UploadState.UPLOAD_CORRUPTED);
    });
    project.sdk.getEventEmitter().addListener('ImageShareInvalidToken', (event: ImageShareInvalidToken) => {
      console.log('#event - DataModel(ImageShareInvalidToken) - event: ', event);
      setUploadingState(UploadState.FATAL);
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadError, {
        source_type: sourceEventType,
        error: 'token_expired',
        product_group: '',
        section_name: '',
      });
    });
    project.sdk.getEventEmitter().addListener('ImageShareExpiredAuthToken', (event: ImageShareExpiredAuthToken) => {
      console.log('#event - DataModel(ImageShareExpiredAuthToken) - event: ', event);
      jsCookies.set(PHS_TOKEN_COOKIE, 'expired token', { domain: ROOT_DOMAIN, expires: new Date(1990) });
      jsCookies.set(USER_TOKEN_COOKIE, 'expired token', { domain: ROOT_DOMAIN, expires: new Date(1990) });
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadError, {
        source_type: sourceEventType,
        error: 'credentials_expired',
        product_group: '',
        section_name: '',
      });
      reloadPage();
    });
    project.sdk.getEventEmitter().addListener('ImageShareFailure', (event: ImageShareFailure) => {
      console.log('#event - DataModel(ImageShareFailure) - event: ', event);
      setUploadingState(UploadState.RETRY);
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadError, {
        source_type: sourceEventType,
        error: 'load_failed',
        product_group: '',
        section_name: '',
      });
    });
    project.sdk.getEventEmitter().addListener('ImageShareUploadFailure', (event: ImageShareUploadFailure) => {
      console.log('#event - DataModel(ImageShareUploadFailure) - event: ', event);
      setUploadingState(UploadState.RETRY_UPLOAD);
      amplitude.trackEvent(AnalyticsEvents.sharePhoto.uploadError, {
        source_type: sourceEventType,
        error: 'load_failed',
        product_group: '',
        section_name: '',
      });
    });
  };

  const unsubscribeEvents = () => {
    project.sdk.getEventEmitter().removeAllListeners('ImageShareStatusUpdate');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareError');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareCorruptedImage');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareInvalidToken');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareExpiredAuthToken');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareFailure');
    project.sdk.getEventEmitter().removeAllListeners('ImageShareUploadFailure');
  };

  const onPressRetryCallback = useCallback(async () => {
    switch (uploadingState) {
      case UploadState.RETRY_UPLOAD:
        return onPressRetryUpload();
      case UploadState.RETRY:
        return onPressRetry();
      case UploadState.UPLOAD_AGAIN:
        return onPressUpload();
      case UploadState.UPLOAD_CORRUPTED:
        return onPressUpload();
      default:
        return Promise.resolve();
    }
  }, [uploadingState, onPressRetryUpload, onPressRetry, onPressUpload]);

  // Configuration for dynamic texts based on state
  const config = useMemo(() => {
    const isSmartphone = sourceEventType === SourcesEventType.Smartphone;
    const translationPrefix = isSmartphone ? 'upload-smartphone' : 'upload-family-friends';
    switch (uploadingState) {
      case UploadState.UPLOAD:
        return {
          title: i18n.t(`${translationPrefix}:title`),
          description: i18n.t(`${translationPrefix}:description`),
          ctaText: i18n.t(`${translationPrefix}:cta`),
          illustrationName: isSmartphone ? ILLUSTRATION_NAMES.Import : ILLUSTRATION_NAMES.ShareFamily,
          isDisabled: isSmartphone && (!settings.projectGuid || !settings.thumbnailSize),
        };
      case UploadState.UPLOADED:
        return {
          title: i18n.t(`${translationPrefix}:uploaded-title`),
          description: i18n.t(`${translationPrefix}:uploaded-description`),
          ctaText: i18n.t(`${translationPrefix}:uploaded-cta`),
          illustrationName: ILLUSTRATION_NAMES.InfoZoom,
          redirectText: !isSmartphone ? i18n.t(`${translationPrefix}:redirect`) : undefined,
          redirectCtaText: !isSmartphone ? i18n.t(`${translationPrefix}:redirect-cta`) : undefined,
        };
      case UploadState.FATAL:
        return {
          title: i18n.t(`${translationPrefix}:error-fatal-title`),
          description: i18n.t(`${translationPrefix}:error-fatal-description`),
        };
      case UploadState.RETRY:
      case UploadState.RETRY_UPLOAD:
        return {
          title: isPrePicker
            ? i18n.t(`${translationPrefix}:error-fatal-title`)
            : i18n.t(`${translationPrefix}:error-retry-title`),
          illustrationName: isPrePicker ? ILLUSTRATION_NAMES.Error : ILLUSTRATION_NAMES.ImportFailed,
          description: i18n.t(`${translationPrefix}:error-retry-description`),
          ctaText: i18n.t(`${translationPrefix}:retry`),
        };
      case UploadState.UPLOAD_AGAIN:
      case UploadState.UPLOAD_CORRUPTED:
        return {
          title: i18n.t(`${translationPrefix}:upload-again-title`),
          description: i18n.t(`${translationPrefix}:upload-again-description`),
          ctaText: i18n.t(`${translationPrefix}:upload-again-cta`),
          corruptedTitle: i18n.t(`${translationPrefix}:upload-corrupted-title`),
          corruptedCta: i18n.t(`${translationPrefix}:upload-corrupted-cta`),
          corruptedUrlText: i18n.t(`${translationPrefix}:upload-corrupted-url`),
        };
      case UploadState.UPLOADING:
        return {
          title: i18n.t(`${translationPrefix}:uploading-title`),
          description: i18n.t(`${translationPrefix}:uploading-description`),
        };
      default:
        return {
          title: '',
          description: '',
        };
    }
  }, [uploadingState, i18n, sourceEventType, settings, isPrePicker]);

  return {
    progress,
    uploadingState,
    style,
    Component: ComponentMap[uploadingState],
    corruptedFiles,
    isLoading,
    settings,
    onPressRedirectToPhotosiCta,
    onPressUpload,
    onPressRetryCallback,
    config,
  };
};
